import { z } from "zod";
import { ROLES } from "./roles";

/** Supported UI languages. `null` in a preference means "browser default". */
export const LOCALES = ["de", "en"] as const;
export type Locale = (typeof LOCALES)[number];

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
export const NAME_MAX_LENGTH = 100;

export const emailSchema = z
  .string({ error: "required" })
  .trim()
  .toLowerCase()
  .pipe(z.email({ error: "email_invalid" }));

export const passwordSchema = z
  .string({ error: "required" })
  .min(PASSWORD_MIN_LENGTH, { error: "password_too_short" })
  .max(PASSWORD_MAX_LENGTH, { error: "password_too_long" });

export const nameSchema = z
  .string({ error: "required" })
  .trim()
  .min(1, { error: "name_required" })
  .max(NAME_MAX_LENGTH, { error: "name_too_long" });

export const roleSchema = z.enum(ROLES, { error: "invalid" });

export const localeSchema = z.enum(LOCALES, { error: "invalid" });

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string({ error: "required" }).min(1, { error: "required" }),
});

export const setupSchema = z.object({
  setupCode: z.string({ error: "required" }).trim().min(1, { error: "setup_code_required" }),
  name: nameSchema,
  email: emailSchema,
  password: passwordSchema,
});

export const INVITATION_EXPIRY_DAYS = [1, 7, 30] as const;
export type InvitationExpiryDays = (typeof INVITATION_EXPIRY_DAYS)[number];

export const createInvitationSchema = z
  .object({
    role: roleSchema,
    email: z
      .union([z.literal(""), emailSchema])
      .optional()
      .transform((value) => (value ? value : undefined)),
    expiresInDays: z
      .union([z.literal(1), z.literal(7), z.literal(30)], { error: "invalid" })
      .default(7),
    /** Also send the link by email (needs `email`). */
    sendEmail: z.boolean({ error: "invalid" }).default(false),
    /** Language of the email; default: the admin's saved language, then `Accept-Language`. */
    locale: localeSchema.optional(),
  })
  .refine((value) => !value.sendEmail || value.email !== undefined, {
    path: ["email"],
    error: "required",
  });

/** Body of `POST /api/invitations/:id/send`; an empty body is `{}`. */
export const sendInvitationSchema = z.object({ locale: localeSchema.optional() });

/** Body of `POST /api/users/:id/reset-link`; a missing or empty body is `{}`. */
export const createResetLinkSchema = z.object({
  sendEmail: z.boolean({ error: "invalid" }).default(false),
  locale: localeSchema.optional(),
});

export const acceptInvitationSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  password: passwordSchema,
});

export const changePasswordSchema = z.object({
  currentPassword: z.string({ error: "required" }).min(1, { error: "required" }),
  newPassword: passwordSchema,
});

export const COLOR_THEMES = ["teal", "blue", "forest", "berry", "amber", "slate"] as const;
export type ColorTheme = (typeof COLOR_THEMES)[number];
export const colorThemeSchema = z.enum(COLOR_THEMES, { error: "invalid" });

export const updateMeSchema = z
  .object({
    locale: localeSchema.nullable().optional(),
    colorTheme: colorThemeSchema.optional(),
  })
  .refine((value) => value.locale !== undefined || value.colorTheme !== undefined, {
    error: "required",
  });

export const setRoleSchema = z.object({
  role: roleSchema,
});

export const resetPasswordSchema = z.object({
  password: passwordSchema,
});

export type SetupInput = z.input<typeof setupSchema>;
export type LoginInput = z.input<typeof loginSchema>;
export type CreateInvitationInput = z.input<typeof createInvitationSchema>;
export type SendInvitationInput = z.input<typeof sendInvitationSchema>;
export type CreateResetLinkInput = z.input<typeof createResetLinkSchema>;
export type AcceptInvitationInput = z.input<typeof acceptInvitationSchema>;
export type ChangePasswordInput = z.input<typeof changePasswordSchema>;
export type UpdateMeInput = z.input<typeof updateMeSchema>;
export type ResetPasswordInput = z.input<typeof resetPasswordSchema>;
